import React, { useMemo, useState } from 'react';
import type { WorkflowVersionInfo } from '../../../types/workflow';
import { Pill } from './WorkflowFormControls';

export interface WorkflowVersionsPanelProps {
  versions: WorkflowVersionInfo[];
  currentVersionId: string | null | undefined;
  isAdmin: boolean;
  onRollback: (versionId: string) => void;
}

const WorkflowVersionsPanel: React.FC<WorkflowVersionsPanelProps> = ({ versions, currentVersionId, isAdmin, onRollback }) => {
  const [query, setQuery] = useState('');
  const sorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...versions]
      .filter((v) => (q ? `${v.name} ${v.version} ${v.version_id} ${v.status}`.toLowerCase().indexOf(q) !== -1 : true))
      .sort((a, b) => b.version - a.version);
  }, [versions, query]);

  if (!versions.length) return <p className="cwf-note">The snapshot has no <code>workflow_versions</code> list.</p>;

  return (
    <div className="cwf-versions">
      <div className="cwf-library__toolbar">
        <input className="cwf-input cwf-input--sm" placeholder="Filter versions…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <span className="cwf-muted">{versions.length} total</span>
      </div>
      <p className="cwf-note">
        Rollback targets a <code>version_id</code>, not a digest. Card libraries are append-only and are not affected by a rollback.
      </p>
      <ul className="cwf-versions__list">
        {sorted.map((v) => {
          const isCurrent = v.version_id === currentVersionId;
          return (
            <li key={v.version_id} className={`cwf-version ${isCurrent ? 'cwf-version--current' : ''}`}>
              <div className="cwf-version__main">
                <span className="cwf-version__number">v{v.version}</span>
                <span className="cwf-version__name" title={v.name}>
                  {v.name}
                </span>
                <Pill tone={v.status === 'published' ? 'ok' : v.status === 'archived' ? 'neutral' : 'accent'}>{v.status}</Pill>
                {isCurrent ? <Pill tone="accent">current</Pill> : null}
              </div>
              <div className="cwf-version__side">
                <code title={v.version_id}>{v.version_id.slice(0, 8)}…</code>
                {isAdmin && !isCurrent ? (
                  <button type="button" className="cwf-btn cwf-btn--xs" onClick={() => onRollback(v.version_id)} title="Roll back to this version">
                    <i className="fas fa-history" aria-hidden="true" /> Roll back
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WorkflowVersionsPanel;
